/* eslint-disable @typescript-eslint/no-explicit-any */

"use client";

import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  alpha,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Avatar,
} from "@mui/material";
import {
  Schedule,
  CheckCircle,
  RocketLaunch,
  DesignServices,
  Build,
  Support,
  Security,
  TrendingUp,
  Groups,
  AccountBalance,
  Payment,
  Lock,
  Analytics,
  Shield,
} from "@mui/icons-material";
import PopoverForm from "@/components/PopoverForm";

export default function Fintech() {
  const theme = useTheme();
  const handleConsult = () => {
    window.open("https://calendly.com/linxarlabs-info", "_blank");
  };

  const painPoints = [
    {
      title: "Security & Compliance",
      description:
        "Meeting strict regulatory requirements and protecting sensitive financial data from breaches.",
    },
    {
      title: "System Integration",
      description:
        "Complex integration with banking systems, payment gateways, and financial data sources.",
    },
    {
      title: "Scalability Challenges",
      description:
        "Handling high transaction volumes and user growth without performance degradation.",
    },
    {
      title: "User Trust & Experience",
      description:
        "Building secure yet user-friendly interfaces that inspire confidence and ease of use.",
    },
  ];

  const solutions = [
    {
      title: "Secure Platform Development",
      description:
        "Bank-grade security with encryption, multi-factor authentication, and compliance frameworks.",
    },
    {
      title: "API & System Integration",
      description:
        "Seamless integration with payment processors, banking APIs, and financial data providers.",
    },
    {
      title: "Scalable Architecture",
      description:
        "Cloud-native solutions designed for high availability and transaction processing at scale.",
    },
    {
      title: "Trust-Focused UX",
      description:
        "Intuitive interfaces that balance security requirements with exceptional user experience.",
    },
  ];

  const keyServices = [
    {
      title: "Fintech Platform Development",
      description: "Secure, scalable financial applications and platforms",
      features: [
        "Digital Banking Apps",
        "Payment Systems",
        "Investment Platforms",
        "Lending Solutions",
      ],
      icon: <Build sx={{ fontSize: 40 }} />,
      color: theme.palette.primary.main,
    },
    {
      title: "Security & Compliance",
      description: "Bank-grade security and regulatory compliance",
      features: [
        "GDPR/PSD2 Compliance",
        "Data Encryption",
        "Fraud Detection",
        "Security Audits",
      ],
      icon: <Lock sx={{ fontSize: 40 }} />,
      color: theme.palette.error.main,
    },
    {
      title: "Payment Integration",
      description: "Seamless payment processing and gateway integration",
      features: [
        "Payment Gateway API",
        "Multi-Currency",
        "Real-time Processing",
        "Recurring Billing",
      ],
      icon: <Payment sx={{ fontSize: 40 }} />,
      color: theme.palette.success.main,
    },
    {
      title: "Automation & Analytics",
      description: "Intelligent automation and financial insights",
      features: [
        "Transaction Automation",
        "Risk Assessment",
        "Financial Reporting",
        "AI-powered Insights",
      ],
      icon: <Analytics sx={{ fontSize: 40 }} />,
      color: theme.palette.info.main,
    },
  ];

  const processSteps = [
    {
      label: "Compliance & Security Assessment",
      description:
        "Analyze regulatory requirements, security standards, and technical specifications for your fintech solution.",
      // icon: <ComplianceIcon />,
      icon: <Shield />,
    },
    {
      label: "Architecture & Development",
      description:
        "Design secure, scalable architecture and develop robust fintech applications with bank-grade security.",
      icon: <DesignServices />,
    },
    {
      label: "Integration & Testing",
      description:
        "Integrate with financial systems, payment gateways, and conduct rigorous security testing.",
      icon: <Build />,
    },
    {
      label: "Security Audit & Launch",
      description:
        "Comprehensive security audits, compliance checks, and controlled production deployment.",
      icon: <RocketLaunch />,
    },
    {
      label: "Ongoing Monitoring & Support",
      description:
        "24/7 monitoring, security updates, and continuous compliance management.",
      icon: <Support />,
    },
  ];

  const whyChoosePoints = [
    {
      icon: <Security sx={{ fontSize: 40 }} />,
      title: "Security-First Approach",
      description:
        "Bank-grade security protocols and compliance expertise built into every solution.",
    },
    {
      icon: <AccountBalance sx={{ fontSize: 40 }} />,
      title: "Financial Industry Expertise",
      description:
        "Deep understanding of banking, payments, lending, and investment technologies.",
    },
    {
      icon: <TrendingUp sx={{ fontSize: 40 }} />,
      title: "Scalable Solutions",
      description:
        "Architecture designed to handle millions of transactions and rapid user growth.",
    },
    {
      icon: <Groups sx={{ fontSize: 40 }} />,
      title: "Trusted Partner",
      description:
        "Proven track record with fintech startups and financial institutions globally.",
    },
  ];

  const complianceStandards = [
    { standard: "PSD2", description: "Payment Services Directive" },
    { standard: "GDPR", description: "Data Protection" },
    { standard: "KYC/AML", description: "Identity Verification" },
    { standard: "PCI DSS", description: "Payment Security" },
    { standard: "SOC 2", description: "Service Organization Controls" },
    { standard: "ISO 27001", description: "Information Security" },
  ];

  return (
    <Box sx={{ minHeight: "100vh", background: "white" }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          color: "white",
          pb: { xs: 8, md: 12 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center" mt={8}>
            <Grid size={{ xs: 12, md: 7 }}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "2.5rem", md: "3.5rem" },
                  fontWeight: 700,
                  lineHeight: 1.2,
                  mb: 3,
                }}
              >
                Secure, Compliant Fintech Solutions Built for Growth
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  mb: 4,
                  opacity: 0.9,
                  lineHeight: 1.6,
                  fontSize: { xs: "1.25rem", md: "1.5rem" },
                }}
              >
                We build secure, compliant fintech platforms—combining
                automation, scalability, and regulatory precision to help
                startups and financial institutions innovate with confidence.
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Schedule />}
                  onClick={handleConsult}
                  sx={{
                    bgcolor: "white",
                    color: theme.palette.primary.main,
                    px: 4,
                    py: 1.5,
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    "&:hover": {
                      bgcolor: alpha("#fff", 0.9),
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Book a Free Consultation
                </Button>
                {/* <Button
                  variant="outlined"
                  size="large"
                  sx={{
                    borderColor: "white",
                    color: "white",
                    px: 4,
                    py: 1.5,
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    "&:hover": {
                      borderColor: "white",
                      bgcolor: alpha("#fff", 0.1),
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  View Fintech Portfolio
                </Button> */}
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <Box
                sx={{
                  background: alpha("#fff", 0.1),
                  borderRadius: 4,
                  p: 4,
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255,255,255,0.2)",
                }}
              >
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                  🔒 Fintech Specialists
                </Typography>
                <List>
                  {[
                    "Bank-grade security protocols",
                    "Payment gateway integration",
                    "Regulatory compliance expertise",
                    "Real-time transaction processing",
                  ].map((item, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckCircle sx={{ color: "white" }} />
                      </ListItemIcon>
                      <ListItemText primary={item} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Pain Points vs Solutions Section */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={6}>
            {/* Pain Points Column */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography
                variant="h3"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "2rem", md: "2.5rem" },
                  color: theme.palette.error.main,
                }}
              >
                Challenges for Fintech Companies
              </Typography>
              <Box sx={{ mt: 4 }}>
                {painPoints.map((point, index) => (
                  <Card
                    key={index}
                    sx={{
                      mb: 3,
                      border: `1px solid ${alpha(theme.palette.error.main, 0.1)}`,
                      background: alpha(theme.palette.error.main, 0.02),
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 2,
                        }}
                      >
                        <Box
                          sx={{
                            color: theme.palette.error.main,
                            flexShrink: 0,
                            mt: 0.5,
                          }}
                        >
                          <WarningIcon />
                        </Box>
                        <Box>
                          <Typography
                            variant="h6"
                            gutterBottom
                            sx={{
                              fontWeight: 600,
                              color: theme.palette.error.main,
                            }}
                          >
                            {point.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {point.description}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Grid>

            {/* Solutions Column */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography
                variant="h3"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "2rem", md: "2.5rem" },
                  color: theme.palette.success.dark,
                }}
              >
                Our Solutions
              </Typography>
              <Box sx={{ mt: 4 }}>
                {solutions.map((solution, index) => (
                  <Card
                    key={index}
                    sx={{
                      mb: 3,
                      border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                      background: alpha(theme.palette.success.main, 0.03),
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateX(8px)",
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 2,
                        }}
                      >
                        <Box
                          sx={{
                            color: theme.palette.success.dark,
                            flexShrink: 0,
                            mt: 0.5,
                          }}
                        >
                          <CheckCircle />
                        </Box>
                        <Box>
                          <Typography
                            variant="h6"
                            gutterBottom
                            sx={{
                              fontWeight: 600,
                              color: theme.palette.success.dark,
                            }}
                          >
                            {solution.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {solution.description}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Compliance Standards Section */}
      <Box
        sx={{
          py: { xs: 6, md: 8 },
          background: alpha(theme.palette.primary.main, 0.02),
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            textAlign="center"
            gutterBottom
            sx={{
              fontWeight: 700,
              mb: 4,
            }}
          >
            Compliance & Security Standards We Implement
          </Typography>
          <Grid container spacing={2} justifyContent="center">
            {complianceStandards.map((standard, index) => (
              <Grid size={{ xs: 6, sm: 4, md: 2 }} key={index}>
                <Card
                  sx={{
                    textAlign: "center",
                    p: 2,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    background: "white",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.15)}`,
                    },
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.primary.main,
                      mb: 1,
                    }}
                  >
                    {standard.standard}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block" }}
                  >
                    {standard.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Key Services Section */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            textAlign="center"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: { xs: "2.5rem", md: "3rem" },
              mb: 2,
            }}
          >
            Key Services for Fintech
          </Typography>
          <Typography
            variant="h6"
            textAlign="center"
            color="text.secondary"
            sx={{ mb: 8, maxWidth: 600, mx: "auto" }}
          >
            Comprehensive solutions tailored for secure, scalable financial
            technology platforms
          </Typography>

          <Grid container spacing={4}>
            {keyServices.map((service, index) => (
              <Grid size={{ xs: 12, md: 6 }} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    border: `1px solid ${alpha(service.color, 0.2)}`,
                    background: "white",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: `0 20px 40px ${alpha(service.color, 0.15)}`,
                    },
                  }}
                >
                  <CardContent sx={{ p: 4, textAlign: "center" }}>
                    <Box
                      sx={{
                        color: service.color,
                        mb: 3,
                      }}
                    >
                      {service.icon}
                    </Box>
                    <Typography
                      variant="h5"
                      gutterBottom
                      sx={{ fontWeight: 600, color: service.color }}
                    >
                      {service.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      paragraph
                    >
                      {service.description}
                    </Typography>
                    <Box sx={{ textAlign: "left" }}>
                      {service.features.map((feature, featureIndex) => (
                        <Typography
                          key={featureIndex}
                          variant="body1"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 0.8,
                            color: "text.primary",
                            fontWeight: 500,
                            "&:before": {
                              content: '"▸"',
                              color: service.color,
                              fontWeight: "bold",
                              mr: 1.5,
                            },
                          }}
                        >
                          {feature}
                        </Typography>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Case Study Highlight */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: alpha(theme.palette.primary.main, 0.02),
        }}
      >
        <Container maxWidth="lg">
          {/* <Card
            sx={{
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              p: { xs: 4, md: 6 },
            }}
          >
            <Grid container spacing={4} alignItems="center">
              <Grid size={{ xs: 12, md: 8 }}>
                <Typography variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
                  Digital Banking Platform Transformation
                </Typography>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Neobank - European Market
                </Typography>

                <Box sx={{ mb: 4 }}>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    paragraph
                    sx={{ lineHeight: 1.7 }}
                  >
                    We developed a simulated neobank prototype to demonstrate
                    our capability in building PSD2-compliant, secure digital
                    banking solutions featuring real-time payments, automated
                    onboarding, and fraud detection modules.
                  </Typography>

                  <Box
                    sx={{
                      background: alpha(theme.palette.success.main, 0.1),
                      border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                      borderRadius: 2,
                      p: 3,
                      maxWidth: 400,
                    }}
                  >
                    <Typography
                      variant="h4"
                      gutterBottom
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.success.main,
                      }}
                    >
                      Key Results
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid size={6}>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                          99.99%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Platform Uptime
                        </Typography>
                      </Grid>
                      <Grid size={6}>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                          &lt;1s
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Transaction Processing
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Box
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    color: "white",
                    borderRadius: 2,
                    p: 4,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 600 }}
                  >
                    Ready for Secure Innovation?
                  </Typography>
                  <PopoverForm
                    triggerElement={
                      <Button
                        variant="contained"
                        size="large"
                        sx={{
                          bgcolor: "white",
                          color: theme.palette.primary.main,
                          mt: 2,
                          px: 3,
                          py: 1,
                          fontWeight: 600,
                          "&:hover": {
                            bgcolor: alpha("#fff", 0.9),
                          },
                        }}
                      >
                        Discuss Your Project
                      </Button>
                    }
                  />
                </Box>
              </Grid>
            </Grid>
          </Card> */}
          {/* Case Study Highlight */}
          <Box
            sx={{
              py: { xs: 8, md: 12 },
              background: alpha(theme.palette.primary.main, 0.02),
            }}
          >
            <Container maxWidth="lg">
              <Card
                sx={{
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  p: { xs: 4, md: 6 },
                }}
              >
                <Grid container spacing={4} alignItems="center">
                  <Grid size={{ xs: 12, md: 8 }}>
                    <Typography
                      variant="h3"
                      gutterBottom
                      sx={{ fontWeight: 700 }}
                    >
                      Digital Banking Platform Prototype
                    </Typography>
                    <Typography
                      variant="h6"
                      color="text.secondary"
                      gutterBottom
                    >
                      Internal Demo Project — LinXar Labs Fintech Sandbox
                    </Typography>

                    <Box sx={{ mb: 4 }}>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        paragraph
                        sx={{ lineHeight: 1.7 }}
                      >
                        As part of our R&D initiative, we designed a simulated
                        neobank platform to showcase how LinXar Labs delivers
                        PSD2-compliant, high-performance digital banking
                        systems. The demo featured real-time payments, automated
                        onboarding, and an AI-driven fraud detection engine —
                        demonstrating our capability to handle secure fintech
                        infrastructure from concept to deployment.
                      </Typography>

                      <Box
                        sx={{
                          background: alpha(theme.palette.success.dark, 0.1),
                          border: `1px solid ${alpha(theme.palette.success.dark, 0.2)}`,
                          borderRadius: 2,
                          p: 3,
                          maxWidth: 400,
                        }}
                      >
                        <Typography
                          variant="h4"
                          gutterBottom
                          sx={{
                            fontWeight: 700,
                            color: theme.palette.success.dark,
                          }}
                        >
                          Simulated Results
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid size={6}>
                            <Typography variant="h5" sx={{ fontWeight: 700 }}>
                              99.99%
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              fontSize={16}
                            >
                              Platform Uptime
                            </Typography>
                          </Grid>
                          <Grid size={6}>
                            <Typography variant="h5" sx={{ fontWeight: 700 }}>
                              &lt;1s
                            </Typography>
                            <Typography
                              variant="body2"
                              fontSize={16}
                              color="text.secondary"
                            >
                              Transaction Processing
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <Box
                      sx={{
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                        color: "white",
                        borderRadius: 2,
                        p: 4,
                        textAlign: "center",
                      }}
                    >
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{ fontWeight: 600 }}
                      >
                        Ready to Build Your Fintech Solution?
                      </Typography>
                      <PopoverForm
                        triggerElement={
                          <Button
                            variant="contained"
                            size="large"
                            sx={{
                              bgcolor: "white",
                              color: theme.palette.primary.main,
                              mt: 2,
                              px: 3,
                              py: 1,
                              fontWeight: 600,
                              "&:hover": {
                                bgcolor: alpha("#fff", 0.9),
                              },
                            }}
                          >
                            Discuss Your Project
                          </Button>
                        }
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Card>
            </Container>
          </Box>
        </Container>
      </Box>

      {/* Process Section */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            textAlign="center"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: { xs: "2.5rem", md: "3rem" },
              mb: 2,
            }}
          >
            Our Fintech Development Process
          </Typography>
          <Typography
            variant="h6"
            textAlign="center"
            color="text.secondary"
            sx={{ mb: 8, maxWidth: 600, mx: "auto" }}
          >
            A security-first methodology for building compliant, scalable
            financial technology platforms
          </Typography>

          <Stepper orientation="vertical" sx={{ maxWidth: 800, mx: "auto" }}>
            {processSteps.map((step) => (
              <Step key={step.label} active={true}>
                <StepLabel
                  StepIconComponent={() => (
                    <Avatar
                      sx={{
                        bgcolor: theme.palette.primary.main,
                        color: "white",
                        width: 40,
                        height: 40,
                      }}
                    >
                      {step.icon}
                    </Avatar>
                  )}
                >
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {step.label}
                  </Typography>
                </StepLabel>
                <StepContent>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mt: 1, lineHeight: 1.7 }}
                  >
                    {step.description}
                  </Typography>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </Container>
      </Box>

      {/* Why LinXar Labs Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: alpha(theme.palette.primary.main, 0.02),
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            textAlign="center"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: { xs: "2.5rem", md: "3rem" },
              mb: 2,
            }}
          >
            Why Choose LinXar Labs for Fintech?
          </Typography>
          <Typography
            variant="h6"
            textAlign="center"
            color="text.secondary"
            sx={{ mb: 8, maxWidth: 600, mx: "auto" }}
          >
            We combine financial technology expertise with enterprise-grade
            security to build platforms you can trust
          </Typography>

          <Grid container spacing={4}>
            {whyChoosePoints.map((point, index) => (
              <Grid size={{ xs: 12, sm: 6 }} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    background: "white",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: `0 12px 30px ${alpha(theme.palette.primary.main, 0.12)}`,
                    },
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box
                      sx={{ display: "flex", alignItems: "flex-start", gap: 3 }}
                    >
                      <Box
                        sx={{
                          color: theme.palette.primary.main,
                          flexShrink: 0,
                        }}
                      >
                        {point.icon}
                      </Box>
                      <Box>
                        <Typography
                          variant="h5"
                          gutterBottom
                          sx={{ fontWeight: 600 }}
                        >
                          {point.title}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          {point.description}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Final CTA Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
          py: { xs: 8, md: 12 },
          textAlign: "center",
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h2"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: { xs: "2.5rem", md: "3rem" },
              mb: 3,
            }}
          >
            Build Your Secure Fintech Future
          </Typography>
          <Typography
            variant="h5"
            color="text.secondary"
            sx={{ mb: 6, lineHeight: 1.6 }}
          >
            Ready to develop compliant, scalable financial technology solutions
            with enterprise-grade security?
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={handleConsult}
            startIcon={<Schedule />}
            sx={{
              px: 4,
              py: 1.5,
              fontSize: "1.1rem",
              fontWeight: 600,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.3)}`,
              },
              transition: "all 0.3s ease",
            }}
          >
            Book a Free Consultation
          </Button>
        </Container>
      </Box>
    </Box>
  );
}

// Custom Icons
function WarningIcon(props: any) {
  return (
    <svg {...props} viewBox="0 0 24 24" fill="currentColor">
      <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
    </svg>
  );
}
